import { ApiError } from '../api/errors';
import { followAnswer } from './follow-answer';
import { backoffDelay, releaseBatch } from './outbox';
import {
    answerRecordFrom,
    findingRecordFrom,
    multiCountRecordFrom,
    orderRecordFrom,
} from './sync-records';
import {
    SYNC_META_KEYS,
    type BatchRecord,
    type ConflictRecord,
    type OutboxOperation,
} from './records';
import { isProblem } from './sync-api';
import type { TenantDatabase } from './tenant-db';
import {
    problemSchema,
    wireAnswerSchema,
    wireFindingSchema,
    wireInspectionSchema,
    wireMultiCountSchema,
    wireOrderSchema,
    type PushResponse,
    type WireProblem,
} from './wire';

/**
 * Het antwoord van `POST /sync/push` verwerken, per bewerking:
 *
 * - `applied` / `unchanged`: klaar. Een antwoord is vanaf nu bij de server
 *   bekend (`confirmed`), dus foto's mogen erheen.
 * - `conflict`: de server had een nieuwere versie. Die wordt lokaal
 *   overgenomen en de inspecteur ziet dat zijn wijziging vervangen is.
 * - `rejected`: de server wees de bewerking af. Hij verdwijnt nooit stil: hij
 *   komt met het `problem` in de conflictenlijst.
 * - een `notice` bij `applied`/`unchanged`: opgeslagen, maar niet precies waar de
 *   inspecteur het zette (de vragenlijst werd intussen vernieuwd). Ook dat komt in
 *   de lijst, als melding en niet als fout.
 */

export interface SettleSummary {
    applied: number;
    conflicts: number;
    rejected: number;
    /** Opgeslagen met een melding: de vragenlijst werd intussen vernieuwd. */
    notices: number;
    /** Opnames die de server na deze batch herberekende. */
    recalculated: string[];
}

export async function settleBatch(
    db: TenantDatabase,
    batch: BatchRecord,
    response: PushResponse,
    now: number,
): Promise<SettleSummary> {
    const summary: SettleSummary = {
        applied: 0,
        conflicts: 0,
        rejected: 0,
        notices: 0,
        recalculated: response.recalculated,
    };

    await db.transaction(
        'rw',
        [
            db.outbox,
            db.batches,
            db.answers,
            db.multiCounts,
            db.findings,
            db.inspections,
            db.orders,
            db.photos,
            db.conflicts,
            db.syncMeta,
        ],
        async () => {
            const operations = await db.outbox.where('batchUuid').equals(batch.batchUuid).toArray();
            const byUuid = new Map(operations.map((operation) => [operation.opUuid, operation]));

            for (const result of response.results) {
                const operation = byUuid.get(result.op_uuid);

                if (!operation?.seq) {
                    continue;
                }

                byUuid.delete(result.op_uuid);
                await db.outbox.delete(operation.seq);

                if (result.result === 'applied' || result.result === 'unchanged') {
                    await confirm(db, operation);
                    summary.applied += 1;

                    /* Opgeslagen, maar niet waar de inspecteur het zette: dat hoort hij. */
                    if (result.notice) {
                        await db.conflicts.put({
                            ...conflictFrom(operation, 'notice', result.server_state ?? null, null, now),
                            id: `${operation.opUuid}:notice`,
                            notice: result.notice,
                        });
                        summary.notices += 1;
                    }

                    continue;
                }

                if (result.result === 'conflict') {
                    await adoptServerState(db, operation, result.server_state);
                    await db.conflicts.put(
                        conflictFrom(
                            operation,
                            'conflict',
                            result.server_state ?? null,
                            result.problem,
                            now,
                        ),
                    );
                    summary.conflicts += 1;
                    continue;
                }

                await db.conflicts.put(
                    conflictFrom(
                        operation,
                        'rejected',
                        result.server_state ?? null,
                        result.problem,
                        now,
                    ),
                );
                summary.rejected += 1;
            }

            /* Een bewerking zonder resultaat gaat gewoon opnieuw mee. */
            for (const operation of byUuid.values()) {
                if (operation.seq !== undefined) {
                    await db.outbox.update(operation.seq, { status: 'pending', batchUuid: null });
                }
            }

            await db.batches.delete(batch.batchUuid);
            await markSettled(db);
        },
    );

    return summary;
}

/** Zie `SYNC_META_KEYS.settled`. */
export async function settledGeneration(db: TenantDatabase): Promise<number> {
    const value = (await db.syncMeta.get(SYNC_META_KEYS.settled))?.value;

    return typeof value === 'number' ? value : 0;
}

async function markSettled(db: TenantDatabase): Promise<void> {
    await db.syncMeta.put({
        key: SYNC_META_KEYS.settled,
        value: (await settledGeneration(db)) + 1,
    });
}

export type BatchFailure = 'retry' | 'released' | 'rejected';

/**
 * De batch als geheel kwam niet aan of werd geweigerd.
 *
 * - Netwerk, time-out, 5xx, 429, 401: dezelfde batch later opnieuw, met backoff.
 * - `409 /problems/batch-mismatch`: deze `batch_uuid` hoort bij andere inhoud;
 *   de bewerkingen gaan terug in de wachtrij en krijgen een nieuwe batch.
 * - Elke andere weigering: alle bewerkingen naar de conflictenlijst, met het
 *   problem - zichtbaar, niet stil weg.
 */
export async function failBatch(
    db: TenantDatabase,
    batch: BatchRecord,
    error: unknown,
    now: number,
): Promise<BatchFailure> {
    if (isProblem(error, 'batch-mismatch')) {
        await releaseBatch(db, batch.batchUuid);

        return 'released';
    }

    /*
     * `409 /problems/conflict` met `Retry-After`: dezelfde batch loopt nog op de
     * server. Straks opnieuw, met dezelfde inhoud; dan volgt het opgeslagen
     * antwoord.
     */
    if (isTransient(error) || isProblem(error, 'conflict')) {
        const attempts = batch.attempts + 1;
        const retryAfter = error instanceof ApiError ? error.retryAfterMs : undefined;

        await db.batches.update(batch.batchUuid, {
            attempts,
            nextAttemptAt: now + (retryAfter ?? backoffDelay(attempts)),
            lastError: error instanceof Error ? error.message : String(error),
        });

        return 'retry';
    }

    const problem = error instanceof ApiError ? problemSchema.parse(error.problem) : null;

    await db.transaction('rw', db.outbox, db.batches, db.conflicts, db.syncMeta, async () => {
        const operations = await db.outbox.where('batchUuid').equals(batch.batchUuid).toArray();

        for (const operation of operations) {
            await db.conflicts.put(conflictFrom(operation, 'rejected', null, problem, now));
        }

        await db.outbox.where('batchUuid').equals(batch.batchUuid).delete();
        await db.batches.delete(batch.batchUuid);
        await markSettled(db);
    });

    return 'rejected';
}

/** Tijdelijk: opnieuw proberen heeft zin. */
export function isTransient(error: unknown): boolean {
    if (!(error instanceof ApiError)) {
        return true;
    }

    return (
        error.retryable ||
        error.kind === 'offline' ||
        error.kind === 'unauthorized' ||
        error.status === 408 ||
        error.status === 426
    );
}

/** Netwerk weg of server onbereikbaar: de rest van deze ronde heeft geen zin. */
export function isUnreachable(error: unknown): boolean {
    return (
        !(error instanceof ApiError) ||
        error.kind === 'offline' ||
        error.kind === 'network' ||
        error.kind === 'timeout'
    );
}

async function confirm(db: TenantDatabase, operation: OutboxOperation): Promise<void> {
    if (operation.entity === 'inspection_answer' && operation.action === 'upsert') {
        await db.answers.update(operation.payload.answer_uuid, { confirmed: 1 });
    }

    if (operation.entity === 'finding' && operation.action === 'upsert') {
        await db.findings.update(operation.payload.finding_uuid, { confirmed: 1 });
    }
}

/**
 * Neemt de versie van de server over - tenzij de inspecteur sindsdien opnieuw
 * iets wijzigde. Die nieuwere bewerking staat dan al in de outbox en de server
 * beslist er zo meteen opnieuw over op `client_updated_at`.
 */
export async function adoptServerState(
    db: TenantDatabase,
    operation: OutboxOperation,
    serverState: unknown,
): Promise<void> {
    const newer = await db.outbox.where('entityKey').equals(operation.entityKey).count();

    if (newer > 0 || serverState === null || serverState === undefined) {
        return;
    }

    const inspectionUuid = operation.inspectionUuid;

    switch (operation.entity) {
        case 'inspection_answer': {
            const parsed = wireAnswerSchema.safeParse(serverState);

            if (!parsed.success || !inspectionUuid) {
                return;
            }

            const record = answerRecordFrom(inspectionUuid, parsed.data);
            await db.answers
                .where('[inspectionUuid+slot]')
                .equals([inspectionUuid, record.slot])
                .filter((answer) => answer.answerUuid !== record.answerUuid)
                .delete();
            await followAnswer(db, await db.answers.get(record.answerUuid), record);
            await db.answers.put(record);

            return;
        }

        case 'multi_count': {
            const parsed = wireMultiCountSchema.safeParse(serverState);

            if (parsed.success && inspectionUuid) {
                await db.multiCounts.put(multiCountRecordFrom(inspectionUuid, parsed.data));
            }

            return;
        }

        case 'finding': {
            const parsed = wireFindingSchema.safeParse(serverState);

            if (parsed.success && inspectionUuid) {
                await db.findings.put(findingRecordFrom(inspectionUuid, parsed.data));
            }

            return;
        }

        case 'inspection': {
            const parsed = wireInspectionSchema
                .pick({ status: true, started_at: true, completed_at: true, notes: true })
                .safeParse(serverState);

            if (parsed.success && inspectionUuid) {
                await db.inspections.update(inspectionUuid, {
                    status: parsed.data.status,
                    startedAt: parsed.data.started_at,
                    completedAt: parsed.data.completed_at,
                    notes: parsed.data.notes,
                });
            }

            return;
        }

        case 'order': {
            const parsed = wireOrderSchema.safeParse(serverState);

            if (parsed.success) {
                await db.orders.put(orderRecordFrom(parsed.data));
            }

            return;
        }
    }
}

function conflictFrom(
    operation: OutboxOperation,
    kind: 'conflict' | 'rejected' | 'notice',
    serverState: unknown,
    problem: WireProblem | null,
    now: number,
): ConflictRecord {
    return {
        id: operation.opUuid,
        kind,
        entity: operation.entity,
        entityKey: operation.entityKey,
        inspectionUuid: operation.inspectionUuid,
        localPayload: operation.payload,
        serverState,
        problem,
        clientUpdatedAt: operation.clientUpdatedAt,
        detectedAt: now,
        acknowledged: 0,
    };
}
